import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { TYPE_META } from "../lib/constants";

const LangSwitch = () => {
  const { lang, setLang } = useI18n();
  return (
    <div className="inline-flex rounded-full border border-wostel-ink/15 p-0.5 text-xs font-medium" data-testid="lang-switch">
      {["en", "fr"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-btn-${l}`}
          className={`px-3 py-1 rounded-full transition ${lang === l ? "bg-wostel-ink text-sand-50" : "text-wostel-ink/70 hover:text-wostel-ink"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export default function Header() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const links = [
    { to: "/", label: t.nav.home, end: true },
    { to: "/stays", label: t.nav.stays },
    { to: "/guides", label: t.nav.guides },
    { to: "/creators", label: t.nav.creator },
    { to: "/contact", label: t.nav.contact },
  ];

  // Hide marketing header on admin pages
  if (loc.pathname.startsWith("/admin")) return null;

  return (
    <header className="sticky top-0 z-50 bg-sand-50/85 backdrop-blur-xl border-b border-sand-300/60" data-testid="site-header">
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img src={TYPE_META.hostel.logo} alt="" className="h-11 w-11" />
          <div className="leading-none">
            <div className="font-serif text-xl tracking-tight">Wostel</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-wostel-ink/60">Djerba</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 font-medium text-sm">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `relative py-1 transition hover:text-wostel-cyan ${isActive ? "text-wostel-cyan" : "text-wostel-ink/80"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangSwitch />
          <Link to="/stays" data-testid="header-book-btn" className="btn-pill bg-wostel-ink text-sand-50 hover:bg-wostel-cyan">
            {t.nav.book}
          </Link>
        </div>

        <button onClick={() => setOpen(!open)} className="lg:hidden p-2" data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-sand-300/60 bg-sand-50">
          <div className="container-x py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                className="text-base font-medium py-2 border-b border-sand-300/60">
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center justify-between pt-2">
              <LangSwitch />
              <Link to="/stays" onClick={() => setOpen(false)} className="btn-pill bg-wostel-ink text-sand-50">
                {t.nav.book}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
